import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api.ts'
import { useAuth } from '../auth/AuthContext.tsx'
import { money, categoryLabel, CATEGORIES, plural } from '../format.ts'

interface MachineRow {
  id: string
  model: string
  category: string
  catalogCode: string
  manufacturerId: string
  manufacturerName: string
  retailPrice: string | null
  retailCurrency: string
  status: string
  // Present only for owners — the API simply omits these for sales.
  purchasePrice?: string | null
  purchaseCurrency?: string | null
  marginPln?: string | null
}

interface Manufacturer {
  id: string
  name: string
  machineCount: number
}

export function CatalogPage() {
  const { user } = useAuth()
  const [machines, setMachines] = useState<MachineRow[]>([])
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([])
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [manufacturerId, setManufacturerId] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const isOwner = user?.role === 'wlasciciel'

  useEffect(() => {
    api
      .get<{ manufacturers: Manufacturer[] }>('/api/manufacturers')
      .then((r) => setManufacturers(r.manufacturers))
      // Only the filter dropdown — the page still works without it, and the
      // machine list below surfaces any real connection problem.
      .catch(() => setManufacturers([]))
  }, [])

  // Debounced so typing doesn't fire a request per keystroke, but stays well
  // inside "instant" (SPEC 5.1).
  useEffect(() => {
    const t = setTimeout(() => {
      const params = new URLSearchParams()
      if (search.trim()) params.set('search', search.trim())
      if (category) params.set('category', category)
      if (manufacturerId) params.set('manufacturerId', manufacturerId)
      setLoading(true)
      api
        .get<{ machines: MachineRow[] }>(`/api/machines?${params}`)
        .then((r) => {
          setMachines(r.machines)
          setError(null)
        })
        .catch((e) => setError(e instanceof Error ? e.message : 'Błąd'))
        .finally(() => setLoading(false))
    }, 150)
    return () => clearTimeout(t)
  }, [search, category, manufacturerId])

  const total = machines.length
  const hasFilters = Boolean(search || category || manufacturerId)

  return (
    <div className="p-6">
      <div className="flex items-baseline justify-between mb-4">
        <h1 className="text-xl font-bold text-slate-800">Katalog maszyn</h1>
        {isOwner && (
          <div className="flex gap-2">
            <Link to="/katalog/nowy" className="text-sm bg-brand-600 text-white px-3 py-1.5 rounded-md hover:bg-brand-700">
              Dodaj produkt
            </Link>
            <Link to="/katalog/import" className="text-sm border border-slate-300 text-slate-700 px-3 py-1.5 rounded-md hover:border-brand-600">
              Wgraj cennik
            </Link>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-3">
        <input
          type="search"
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Szukaj: model, kod katalogowy, opis…"
          className="flex-1 min-w-64 px-3 py-1.5 border border-slate-300 rounded-md text-sm focus:border-brand-600"
        />
        <select
          value={manufacturerId}
          onChange={(e) => setManufacturerId(e.target.value)}
          className="px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white"
        >
          <option value="">Wszyscy producenci</option>
          {manufacturers.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name} ({m.machineCount})
            </option>
          ))}
        </select>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white"
        >
          <option value="">Wszystkie kategorie</option>
          {CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
        {hasFilters && (
          <button
            onClick={() => {
              setSearch('')
              setCategory('')
              setManufacturerId('')
            }}
            className="text-sm text-slate-500 hover:text-slate-700 px-2 underline"
          >
            Wyczyść
          </button>
        )}
      </div>

      {error && (
        <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-3">
          {error}
        </div>
      )}

      <div className="text-xs text-slate-500 mb-2">
        {loading ? 'Szukam…' : `${total} ${plural(total, 'maszyna', 'maszyny', 'maszyn')}`}
      </div>

      {/* Dense table — SPEC 5.7 */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase text-slate-500">
              <th className="px-3 py-2 font-medium">Producent</th>
              <th className="px-3 py-2 font-medium">Model</th>
              <th className="px-3 py-2 font-medium">Kategoria</th>
              <th className="px-3 py-2 font-medium">Kod katalogowy</th>
              <th className="px-3 py-2 font-medium text-right">Cena detaliczna</th>
              {/* Owner-only columns. Sales never receives this data from the API,
                  and the database wouldn't hand it over either. */}
              {isOwner && <th className="px-3 py-2 font-medium text-right">Cena zakupu</th>}
              {isOwner && <th className="px-3 py-2 font-medium text-right">Marża</th>}
            </tr>
          </thead>
          <tbody>
            {machines.map((m) => (
              <tr key={m.id} className="border-b border-slate-100 last:border-0 hover:bg-brand-50/50">
                <td className="px-3 py-1.5 text-slate-500">{m.manufacturerName}</td>
                <td className="px-3 py-1.5">
                  <Link to={`/katalog/${m.id}`} className="text-brand-700 hover:underline font-medium">
                    {m.model}
                  </Link>
                  {m.status === 'archiwalna' && (
                    <span className="ml-2 text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">archiwalna</span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-slate-600">{categoryLabel(m.category)}</td>
                <td className="px-3 py-1.5 font-mono text-xs text-slate-500">{m.catalogCode}</td>
                <td className="px-3 py-1.5 text-right tabular">{money(m.retailPrice, m.retailCurrency)}</td>
                {isOwner && (
                  <td className="px-3 py-1.5 text-right tabular text-slate-600">
                    {money(m.purchasePrice, m.purchaseCurrency ?? 'PLN')}
                  </td>
                )}
                {isOwner && (
                  <td className="px-3 py-1.5 text-right tabular">
                    {m.marginPln ? (
                      <span className={Number(m.marginPln) > 0 ? 'text-green-700' : 'text-red-700'}>
                        {money(m.marginPln, m.retailCurrency)}
                      </span>
                    ) : (
                      // Never a zero here: a margin we can't compute (different
                      // currencies, no FX rate) is unknown, not nil.
                      <span className="text-slate-400" title="Różne waluty — marża wymaga kursu">
                        —
                      </span>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {!loading && machines.length === 0 && (
              <tr>
                <td colSpan={isOwner ? 7 : 5} className="px-3 py-8 text-center text-slate-400">
                  {hasFilters ? 'Nic nie znaleziono. Zmień filtry.' : 'Katalog jest pusty — wgraj cennik.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

